import {useEffect, useState} from 'react';
import appStore from 'client/AppStore';
import {useShallow} from 'zustand/react/shallow';

import {Set} from 'immutable';
import {addToast} from '@albert-io/atomic';

import {getFolderQuestionSetsQuery} from 'client/FolderPracticeView/folderPracticeView.queries';

import getSmartSelectQuery from 'client/components/AssignModal/AssignModal.queries';

import {getQuestionSetsByGuideLevelIdQuery} from 'resources/Question/QuestionSet.queries';

import {getQuestionSetsQuery} from 'client/CreateAssignmentV2/utils/createAssignment.queries';

import {
  getAssignmentById,
  getAssignmentQuestionSets
} from '../../../ViewAssignment/ViewAssignment.queries';
import {ViewLocations} from '../../../store/slices/Navigation.store';
import {useContentDiscoveryStore} from '../../../store';

export const fetchQuestionSetsWithIds = async ({
  resourceType,
  resourceId,
  excludedQuestionIds,
  includedQuestionIds,
  numSmartAssignQuestions
}: {
  resourceType: string;
  resourceId: string | null;
  excludedQuestionIds?: string[];
  includedQuestionIds?: string[];
  numSmartAssignQuestions?: number;
}) => {
  try {
    let questions;
    if (numSmartAssignQuestions && numSmartAssignQuestions > 0) {
      const smartQuery = getSmartSelectQuery(numSmartAssignQuestions, resourceId, resourceType);
      const queryResult = await smartQuery.getResourcePromise();

      const questionSetIds = queryResult
        .getMeta()
        .getSmartSelectResults()
        .map((question) => {
          const {id} = question.toJS();
          return id;
        });

      const idSet = Set(questionSetIds).toArray();

      questions =
        resourceType === 'folder'
          ? await getFolderQuestionSetsQuery(
              resourceId,
              numSmartAssignQuestions,
              idSet
            ).getResourcePromise()
          : await getQuestionSetsByGuideLevelIdQuery(
              resourceId,
              numSmartAssignQuestions,
              idSet
            ).getResourcePromise();
    } else if (excludedQuestionIds && excludedQuestionIds.length > 0) {
      const resourceStr = resourceType === 'folder' ? 'templates_v1' : 'guide_levels_v2';
      questions = await getQuestionSetsQuery(
        resourceStr,
        resourceId,
        excludedQuestionIds
      ).getResourcePromise();
    } else {
      questions =
        resourceType === 'folder'
          ? await getFolderQuestionSetsQuery(
              resourceId,
              'infinite',
              includedQuestionIds
            ).getResourcePromise()
          : await getQuestionSetsByGuideLevelIdQuery(
              resourceId,
              'infinite',
              includedQuestionIds
            ).getResourcePromise();
    }

    return questions;
  } catch (_e) {
    addToast({
      message: 'Something went wrong with fetching your questions.',
      title: 'Error fetching questions',
      color: 'negative'
    });
    return null;
  }
};

export const handleAssignmentBuilderInitializationHook = (query, pathname) => {
  const {
    setfolderId,
    fetchAndSetFolder,
    setLocation,
    setInitialName,
    setSelectedQuestionSets,
    selectedQuestionSets,
    isDraft,
    numSmartAssignQuestions,
    legacyItemId,
    setLegacyItemId,
    setNumSmartAssignQuestions,
    legacyQuestionsIds,
    setLegacyQuestionsIds,
    legacyQuestionsIdsExcluded,
    setLegacyQuestionsIdsExcluded,
    setLegacyItemType,
    legacyItemType,
    setLegacyQuestionSets
  } = useContentDiscoveryStore(
    useShallow((state) => ({
      setfolderId: state.folderSlice.setFolderId,
      fetchAndSetFolder: state.folderSlice.fetchAndSetFolderOrError,
      setLocation: state.navigation.setViewLocation,
      setInitialName: state.navigation.setInitialName,
      setSelectedQuestionSets: state.draft.setSelectedQuestionSets,
      selectedQuestionSets: state.draft.selectedQuestionSets,
      isDraft: state.draft.isDraft,
      numSmartAssignQuestions: state.draft.numSmartAssignQuestions,
      legacyItemId: state.draft.legacyItemId,
      setLegacyItemId: state.draft.setLegacyItemId,
      setNumSmartAssignQuestions: state.draft.setNumSmartAssignQuestions,
      legacyQuestionsIds: state.draft.legacyQuestionsIds,
      setLegacyQuestionsIds: state.draft.setLegacyQuestionsIds,
      legacyQuestionsIdsExcluded: state.draft.legacyQuestionsIdsExcluded,
      setLegacyQuestionsIdsExcluded: state.draft.setLegacyQuestionsIdsExcluded,
      legacyItemType: state.draft.legacyItemType,
      setLegacyItemType: state.draft.setLegacyItemType,
      legacyQuestionSets: state.draft.legacyQuestionSets,
      setLegacyQuestionSets: state.draft.setLegacyQuestionSets
    }))
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchQuestionSetsOnMount = async (type) => {
      setIsLoading(true);
      try {
        let questions;
        if (numSmartAssignQuestions > 0) {
          questions = await fetchQuestionSetsWithIds({
            resourceType: type,
            resourceId: legacyItemId,
            numSmartAssignQuestions
          });
        } else if (legacyQuestionsIdsExcluded && legacyQuestionsIdsExcluded.length > 0) {
          questions = await fetchQuestionSetsWithIds({
            resourceType: type,
            resourceId: legacyItemId,
            excludedQuestionIds: legacyQuestionsIdsExcluded
          });
        } else {
          questions = await fetchQuestionSetsWithIds({
            resourceType: type,
            resourceId: legacyItemId,
            includedQuestionIds: legacyQuestionsIds
          });
        }

        setSelectedQuestionSets(questions);
        setLegacyQuestionSets(questions);
      } catch (_e) {
        addToast({
          message: 'Something went wrong with fetching your questions.',
          title: 'Error fetching questions',
          color: 'negative'
        });
      }
      setIsLoading(false);
      setLegacyItemId(null);
      setNumSmartAssignQuestions(0);
      setLegacyQuestionsIds([]);
      setLegacyQuestionsIdsExcluded([]);
      setLegacyItemType(null);
    };

    // Handles fetching folder or assignment data and calling respective setters
    const fetchData = async (id: string, type: 'folder' | 'assignment') => {
      setIsLoading(true);
      try {
        if (type === 'folder') {
          setfolderId(id);
          if (selectedQuestionSets.size === 0) {
            const {questionSets, errorLoadingFolder, error} = await fetchAndSetFolder(id);
            if (errorLoadingFolder) {
              throw error;
            }
            setSelectedQuestionSets(questionSets);
          }
        } else if (type === 'assignment') {
          const [fetchedAssignment, fetchedAssignmentQuestionSets] = await Promise.all([
            getAssignmentById(id, isDraft),
            getAssignmentQuestionSets(id, isDraft)
          ]);
          setSelectedQuestionSets(fetchedAssignmentQuestionSets);
          setInitialName(fetchedAssignment.getName());
        }
      } catch (_err) {
        // TODO: error handling for failed prefilling attempts
      } finally {
        setIsLoading(false);
      }
    };

    const location = getViewLocationFromPath(pathname);
    setLocation(location);
    // prefill logic from query params
    let {folderId: prefillFolderId} = query; // default to query param for folder id
    const {assignmentId: queryAssignmentId} = query;

    if (legacyItemId) {
      fetchQuestionSetsOnMount(legacyItemType);
    } else if (location === 'IN_ASSIGNMENT_BUILDER') {
      if (prefillFolderId?.length > 0) {
        fetchData(prefillFolderId, 'folder');
      }
      // TODO: preload from assignment id in query
      else if (queryAssignmentId?.length) {
        fetchData(queryAssignmentId, 'assignment');
      }
    } else if (location === 'IN_FOLDER_VIEW') {
      prefillFolderId = appStore.routerProps.params.folderId; // use param instead if in folder view
      fetchData(prefillFolderId, 'folder');
    }
    // TODO: consider moving assignment view prefil logic here
  }, [
    query,
    pathname,
    setLocation,
    fetchAndSetFolder,
    setInitialName,
    setSelectedQuestionSets,
    setfolderId,
    isDraft,
    legacyItemId,
    numSmartAssignQuestions,
    setLegacyItemId,
    setNumSmartAssignQuestions,
    setLegacyQuestionsIds,
    setLegacyQuestionsIdsExcluded,
    legacyQuestionsIds,
    legacyQuestionsIdsExcluded,
    legacyItemType,
    setLegacyItemType
  ]);

  // set selected question sets from fetched folder (if fetched)

  return {
    isLoading
  };
};

const getViewLocationFromPath = (pathname: string) => {
  let location: ViewLocations = 'IN_ASSIGNMENT_BUILDER';
  if (pathname.includes('folder')) {
    location = 'IN_FOLDER_VIEW';
  }
  return location;
};

export default handleAssignmentBuilderInitializationHook;
